import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaGem,
  FaShieldAlt,
  FaFemale,
  FaQuoteLeft,
  FaQuoteRight,
  FaUserGraduate,
  FaBriefcase,
  FaChartLine,
  FaCheckCircle,
  FaHandHoldingHeart,
  FaShoppingBag,
  FaGlobe,
  FaBuilding,
  FaHandshake,
  FaArrowDown
} from 'react-icons/fa';

import { GiTShirt } from 'react-icons/gi';

/* ============================================================
   LOCAL GLITZ IMAGES
   ============================================================ */

import glitzLogo from '../logos/glitz/Glitzlogo.png';

import glitz1 from '../logos/glitz/glitz1.png';
import glitz2 from '../logos/glitz/glitz2.png';
import glitz3 from '../logos/glitz/glitz3.png';
import glitz4 from '../logos/glitz/glitz4.png';
import glitz5 from '../logos/glitz/glitz5.png';


export default function GlitzBrand() {

  const navigate = useNavigate();

  /* ============================================================
     SCROLL REVEAL ANIMATION
     ============================================================ */

  useEffect(() => {

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            entry.target.classList.add('glitz-show');
          }

        });

      },
      {
        threshold: 0.10
      }
    );

    const elements = document.querySelectorAll(
      '.glitz-reveal, .glitz-left-reveal, .glitz-right-reveal, .glitz-scale'
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };

  }, []);


  return (

    <div className="glitz-page">

      <style>{`

        /* ======================================================
           RESET
           ====================================================== */

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
          background: #07090d;
        }

        button,
        img {
          user-select: none;
        }


        /* ======================================================
           MAIN PAGE
           ====================================================== */

        .glitz-page {

          width: 100%;

          min-height: 100vh;

          background:
            radial-gradient(
              circle at 15% 10%,
              rgba(212,175,55,0.09),
              transparent 30%
            ),
            radial-gradient(
              circle at 85% 30%,
              rgba(212,175,55,0.05),
              transparent 28%
            ),
            linear-gradient(
              135deg,
              #06080b 0%,
              #0b0f15 50%,
              #07090d 100%
            );

          color: #fff;

          font-family:
            Georgia,
            'Times New Roman',
            serif;

          overflow-x: hidden;
        }


        /* ======================================================
           GOLD SCROLLBAR
           ====================================================== */

        ::-webkit-scrollbar {
          width: 7px;
        }

        ::-webkit-scrollbar-track {
          background: #080b10;
        }

        ::-webkit-scrollbar-thumb {
          background: #b9972e;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #e0c35d;
        }


        /* ======================================================
           ANIMATIONS
           ====================================================== */

        .glitz-reveal {

          opacity: 0;

          transform:
            translateY(60px);

          transition:
            opacity 0.9s ease,
            transform 0.9s ease;
        }


        .glitz-left-reveal {

          opacity: 0;

          transform:
            translateX(-80px);

          transition:
            opacity 1s ease,
            transform 1s ease;
        }


        .glitz-right-reveal {

          opacity: 0;

          transform:
            translateX(80px);

          transition:
            opacity 1s ease,
            transform 1s ease;
        }


        .glitz-scale {

          opacity: 0;

          transform:
            scale(0.90);

          transition:
            opacity 1s ease,
            transform 1s ease;
        }


        .glitz-show {

          opacity: 1 !important;

          transform:
            translate(0)
            scale(1) !important;
        }


        /* ======================================================
           TOP NAVIGATION
           ====================================================== */

        .glitz-navigation {

          position: relative;

          width: 100%;

          padding:
            18px 35px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          border-bottom:
            1px solid
            rgba(212,175,55,0.18);

          background:
            rgba(5,8,12,0.94);

          backdrop-filter:
            blur(14px);

          z-index: 50;
        }


        .glitz-nav-brand {

          display: flex;

          align-items: center;

          gap: 12px;
        }


        .glitz-nav-brand img {

          width: 45px;

          height: 45px;

          object-fit: contain;
        }


        .glitz-nav-brand span {

          color: #e2c667;

          font-size: 17px;

          letter-spacing: 2px;

          font-weight: 600;
        }


        .glitz-back-button {

          display: inline-flex;

          align-items: center;

          gap: 9px;

          border:
            1px solid #d4af37;

          background:
            transparent;

          color: #d4af37;

          padding:
            10px 18px;

          border-radius: 4px;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size: 12px;

          font-weight: 700;

          cursor: pointer;

          transition:
            all 0.35s ease;
        }


        .glitz-back-button:hover {

          background:
            #d4af37;

          color: #080a0e;

          transform:
            translateX(-4px);

          box-shadow:
            0 8px 25px
            rgba(212,175,55,0.25);
        }


        /* ======================================================
           HERO
           ====================================================== */

        .glitz-hero {

          position: relative;

          width: 100%;

          min-height:
            calc(100vh - 82px);

          display: grid;

          grid-template-columns:
            31%
            40%
            29%;

          gap: 0;

          overflow: hidden;

          border-bottom:
            1px solid
            rgba(212,175,55,0.35);
        }


        .glitz-hero::before {

          content: '';

          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              90deg,
              rgba(212,175,55,0.04),
              transparent 40%,
              rgba(212,175,55,0.025)
            );

          pointer-events: none;
        }


        /* ======================================================
           HERO LEFT
           ====================================================== */

        .glitz-hero-left {

          position: relative;

          display: flex;

          flex-direction: column;

          justify-content: center;

          padding:
            70px 7vw 70px 7vw;

          border-right:
            1px solid
            rgba(212,175,55,0.17);

          z-index: 2;
        }


        .glitz-logo {

          width:
            clamp(70px, 8vw, 115px);

          height:
            clamp(70px, 8vw, 115px);

          object-fit: contain;

          margin-bottom: 20px;

          filter:
            drop-shadow(
              0 10px 25px
              rgba(212,175,55,0.25)
            );

          animation:
            logoFloat 4s ease-in-out infinite;
        }


        @keyframes logoFloat {

          0%,
          100% {
            transform:
              translateY(0);
          }

          50% {
            transform:
              translateY(-10px);
          }
        }


        .glitz-brand-name {

          margin: 0;

          color:
            #e7c96a;

          font-size:
            clamp(60px, 7vw, 110px);

          line-height:
            0.82;

          font-weight:
            400;

          font-style:
            italic;

          letter-spacing:
            -4px;

          text-shadow:
            0 5px 25px
            rgba(212,175,55,0.18);
        }


        .glitz-brand-tagline {

          margin:
            22px 0 0;

          color:
            #f3f3f3;

          font-size:
            clamp(16px, 1.5vw, 23px);

          letter-spacing:
            2px;

          font-style:
            italic;
        }


        .glitz-divider {

          width:
            190px;

          height:
            1px;

          margin:
            24px 0;

          background:
            linear-gradient(
              90deg,
              #d4af37,
              transparent
            );

          position:
            relative;
        }


        .glitz-divider::after {

          content:
            '✦';

          position:
            absolute;

          left:
            42%;

          top:
            -10px;

          color:
            #d4af37;

          font-size:
            16px;
        }


        .glitz-description {

          max-width:
            540px;

          margin:
            0;

          color:
            #c4c8cf;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            clamp(12px, 1vw, 15px);

          line-height:
            1.9;
        }


        .glitz-slogan {

          margin-top:
            28px;

          padding-left:
            18px;

          border-left:
            3px solid
            #d4af37;

          color:
            #e0c366;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            clamp(13px, 1.1vw, 17px);

          font-weight:
            800;

          line-height:
            1.7;

          letter-spacing:
            1px;
        }


        /* ======================================================
           HERO IMAGE
           ====================================================== */

        .glitz-main-image {

          position:
            relative;

          min-height:
            700px;

          overflow:
            hidden;

          background:
            #000;
        }


        .glitz-main-image::after {

          content:
            '';

          position:
            absolute;

          inset:
            0;

          background:
            linear-gradient(
              180deg,
              transparent 60%,
              rgba(0,0,0,0.5)
            );

          pointer-events:
            none;
        }


        .glitz-main-image img {

          width:
            100%;

          height:
            100%;

          min-height:
            700px;

          object-fit:
            cover;

          display:
            block;

          transition:
            transform 1.2s ease;
        }


        .glitz-main-image:hover img {

          transform:
            scale(1.06);
        }


        /* ======================================================
           HERO RIGHT
           ====================================================== */

        .glitz-hero-right {

          display:
            flex;

          flex-direction:
            column;

          justify-content:
            center;

          padding:
            60px 5vw;

          background:
            linear-gradient(
              145deg,
              rgba(17,22,30,0.95),
              rgba(8,11,16,0.95)
            );
        }


        .glitz-feature-box {

          padding:
            25px;

          border:
            1px solid
            rgba(212,175,55,0.45);

          border-radius:
            12px;

          background:
            rgba(8,11,16,0.55);

          box-shadow:
            0 20px 60px
            rgba(0,0,0,0.3);
        }


        .glitz-feature {

          display:
            flex;

          align-items:
            center;

          gap:
            15px;

          padding:
            14px 10px;

          transition:
            all 0.35s ease;
        }


        .glitz-feature + .glitz-feature {

          border-top:
            1px solid
            rgba(255,255,255,0.05);
        }


        .glitz-feature:hover {

          transform:
            translateX(8px);

          color:
            #d4af37;
        }


        .glitz-feature-icon {

          width:
            43px;

          height:
            43px;

          flex-shrink:
            0;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          border:
            1px solid
            #d4af37;

          border-radius:
            50%;

          color:
            #d4af37;

          font-size:
            17px;
        }


        .glitz-feature-text {

          color:
            #e8e8e8;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            12px;

          font-weight:
            700;

          letter-spacing:
            1px;
        }


        .glitz-redefined {

          margin-top:
            28px;

          padding:
            25px;

          text-align:
            center;

          border:
            1px solid
            #d4af37;

          border-radius:
            12px;

          background:
            linear-gradient(
              135deg,
              rgba(212,175,55,0.07),
              transparent
            );

          transition:
            all 0.4s ease;
        }


        .glitz-redefined:hover {

          transform:
            translateY(-6px);

          box-shadow:
            0 20px 45px
            rgba(212,175,55,0.12);
        }


        .glitz-redefined small {

          display:
            block;

          color:
            #d4af37;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            10px;

          font-weight:
            700;

          letter-spacing:
            2px;
        }


        .glitz-redefined h3 {

          margin:
            7px 0 0;

          color:
            #f1dda0;

          font-size:
            35px;

          font-style:
            italic;

          font-weight:
            400;
        }


        /* ======================================================
           COLLECTION
           ====================================================== */

        .glitz-collection {

          width:
            100%;

          padding:
            90px 6vw;

          background:
            linear-gradient(
              180deg,
              #0b1017,
              #0d1219
            );
        }


        .glitz-heading {

          text-align:
            center;

          margin-bottom:
            50px;
        }


        .glitz-heading span {

          display:
            block;

          color:
            #bfa44c;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            11px;

          letter-spacing:
            4px;

          margin-bottom:
            10px;
        }


        .glitz-heading h2 {

          margin:
            0;

          color:
            #e2c76c;

          font-size:
            clamp(28px, 3vw, 45px);

          font-weight:
            500;

          letter-spacing:
            2px;
        }


        .glitz-heading-line {

          width:
            90px;

          height:
            2px;

          margin:
            18px auto 0;

          background:
            #d4af37;
        }


        .glitz-products {

          width:
            100%;

          display:
            grid;

          grid-template-columns:
            repeat(3, 1fr);

          gap:
            28px;
        }


        .glitz-product {

          overflow:
            hidden;

          background:
            #111720;

          border:
            1px solid
            rgba(212,175,55,0.25);

          border-radius:
            10px;

          transition:
            all 0.5s ease;
        }


        .glitz-product:hover {

          transform:
            translateY(-12px);

          border-color:
            #d4af37;

          box-shadow:
            0 30px 60px
            rgba(0,0,0,0.45);
        }


        .glitz-product-title {

          padding:
            17px;

          margin:
            0;

          text-align:
            center;

          color:
            #d4af37;

          background:
            #090d12;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            13px;

          letter-spacing:
            1.5px;
        }


        .glitz-product-image {

          height:
            360px;

          overflow:
            hidden;
        }


        .glitz-product-image img {

          width:
            100%;

          height:
            100%;

          object-fit:
            cover;

          display:
            block;

          transition:
            transform 0.8s ease;
        }


        .glitz-product:hover
        .glitz-product-image img {

          transform:
            scale(1.08);
        }


        .glitz-product-body {

          padding:
            22px;

          min-height:
            135px;
        }


        .glitz-product-body p {

          margin:
            0;

          color:
            #bfc4cb;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            13px;

          line-height:
            1.8;
        }


        /* ======================================================
           EMPOWERMENT
           ====================================================== */

        .glitz-empowerment {

          width:
            100%;

          padding:
            100px 6vw;

          display:
            grid;

          grid-template-columns:
            28%
            42%
            30%;

          gap:
            35px;

          background:
            #080c11;

          border-top:
            1px solid
            rgba(212,175,55,0.18);

          border-bottom:
            1px solid
            rgba(212,175,55,0.18);
        }


        .glitz-empowerment-image {

          min-height:
            400px;

          overflow:
            hidden;

          border-radius:
            8px;

          border:
            1px solid
            rgba(212,175,55,0.35);
        }


        .glitz-empowerment-image img {

          width:
            100%;

          height:
            100%;

          min-height:
            400px;

          object-fit:
            cover;

          display:
            block;

          transition:
            transform 0.8s ease;
        }


        .glitz-empowerment-image:hover img {

          transform:
            scale(1.08);
        }


        .glitz-empowerment-content {

          display:
            flex;

          flex-direction:
            column;

          justify-content:
            center;
        }


        .glitz-empowerment-content small {

          color:
            #bda64f;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            11px;

          letter-spacing:
            3px;
        }


        .glitz-empowerment-content h2 {

          margin:
            10px 0 0;

          color:
            #d8bd5d;

          font-size:
            clamp(25px, 3vw, 42px);

          font-weight:
            500;

          letter-spacing:
            2px;
        }


        .glitz-empowerment-line {

          width:
            90px;

          height:
            2px;

          margin:
            20px 0;

          background:
            #d4af37;
        }


        .glitz-empowerment-content > p {

          color:
            #c0c5cc;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            14px;

          line-height:
            1.9;

          margin:
            0;
        }


        .glitz-quote {

          position:
            relative;

          margin-top:
            25px;

          padding:
            25px 35px;

          border:
            1px solid
            rgba(212,175,55,0.55);

          border-radius:
            8px;

          text-align:
            center;

          background:
            rgba(212,175,55,0.03);
        }


        .glitz-quote svg {

          position:
            absolute;

          color:
            #d4af37;

          opacity:
            0.6;

          font-size:
            13px;
        }


        .glitz-quote-left {

          top:
            12px;

          left:
            12px;
        }


        .glitz-quote-right {

          right:
            12px;

          bottom:
            12px;
        }


        .glitz-quote p {

          margin:
            0;

          color:
            #ead791;

          font-size:
            15px;

          line-height:
            1.8;

          font-style:
            italic;
        }


        /* ======================================================
           PILLARS
           ====================================================== */

        .glitz-pillars {

          display:
            flex;

          flex-direction:
            column;

          justify-content:
            center;

          gap:
            28px;
        }


        .glitz-pillar {

          display:
            flex;

          gap:
            15px;

          align-items:
            flex-start;

          transition:
            transform 0.35s ease;
        }


        .glitz-pillar:hover {

          transform:
            translateX(8px);
        }


        .glitz-pillar-icon {

          width:
            50px;

          height:
            50px;

          flex-shrink:
            0;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          border:
            1px solid
            #d4af37;

          border-radius:
            50%;

          color:
            #d4af37;
        }


        .glitz-pillar h4 {

          margin:
            0;

          color:
            #d4af37;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            12px;

          letter-spacing:
            1px;
        }


        .glitz-pillar p {

          margin:
            6px 0 0;

          color:
            #aeb4bd;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            11px;

          line-height:
            1.6;
        }


        /* ======================================================
           VALUES
           ====================================================== */

        .glitz-values {

          display:
            grid;

          grid-template-columns:
            repeat(5, 1fr);

          width:
            100%;

          background:
            #0c1117;

          border-bottom:
            1px solid
            rgba(212,175,55,0.25);
        }


        .glitz-value {

          position:
            relative;

          min-height:
            145px;

          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          justify-content:
            center;

          text-align:
            center;

          gap:
            12px;

          color:
            #d4af37;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            10px;

          font-weight:
            700;

          letter-spacing:
            0.7px;

          line-height:
            1.5;

          transition:
            all 0.4s ease;
        }


        .glitz-value::after {

          content:
            '';

          position:
            absolute;

          right:
            0;

          top:
            25%;

          width:
            1px;

          height:
            50%;

          background:
            rgba(212,175,55,0.25);
        }


        .glitz-value:hover {

          background:
            rgba(212,175,55,0.06);

          transform:
            translateY(-5px);
        }


        .glitz-value svg {

          color:
            #e6cc72;

          font-size:
            28px;
        }


        /* ======================================================
           MOTTO
           ====================================================== */

        .glitz-motto {

          padding:
            35px 20px;

          text-align:
            center;

          background:
            linear-gradient(
              90deg,
              #080b10,
              #111720,
              #080b10
            );

          border-bottom:
            1px solid
            rgba(212,175,55,0.25);
        }


        .glitz-motto h2 {

          margin:
            0;

          color:
            #d9bd5b;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            clamp(15px, 2vw, 25px);

          letter-spacing:
            2px;

          line-height:
            1.6;
        }


        /* ======================================================
           FOOTER
           ====================================================== */

        .glitz-footer {

          width:
            100%;

          display:
            grid;

          grid-template-columns:
            120px
            1fr
            1.8fr;

          align-items:
            center;

          gap:
            35px;

          padding:
            35px 6vw;

          background:
            #06090d;
        }


        .glitz-number {

          width:
            100px;

          height:
            100px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          color:
            #0b0e12;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            50px;

          font-weight:
            900;

          background:
            linear-gradient(
              145deg,
              #c69d2d,
              #f0d574
            );

          clip-path:
            polygon(
              0 0,
              100% 0,
              80% 100%,
              0 100%
            );
        }


        .glitz-company-name {

          display:
            block;

          color:
            #eee0a4;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            17px;

          font-weight:
            800;

          letter-spacing:
            1px;
        }


        .glitz-company-tag {

          display:
            block;

          margin-top:
            8px;

          color:
            #d4af37;

          font-size:
            13px;

          font-style:
            italic;
        }


        .glitz-footer-links {

          display:
            grid;

          grid-template-columns:
            repeat(5, 1fr);
        }


        .glitz-footer-item {

          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          justify-content:
            center;

          gap:
            8px;

          min-height:
            70px;

          color:
            #aeb3ba;

          text-align:
            center;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          font-size:
            8px;

          line-height:
            1.5;

          border-left:
            1px solid
            rgba(212,175,55,0.2);

          transition:
            all 0.3s ease;
        }


        .glitz-footer-item:hover {

          color:
            #d4af37;

          background:
            rgba(212,175,55,0.04);
        }


        .glitz-footer-item svg {

          color:
            #d4af37;

          font-size:
            22px;
        }


        /* ======================================================
           LARGE TABLET
           ====================================================== */

        @media (max-width: 1100px) {

          .glitz-hero {

            grid-template-columns:
              35%
              38%
              27%;
          }

          .glitz-hero-left {

            padding-left:
              4vw;

            padding-right:
              3vw;
          }

          .glitz-hero-right {

            padding-left:
              3vw;

            padding-right:
              3vw;
          }

          .glitz-empowerment {

            grid-template-columns:
              30%
              40%
              30%;

            padding:
              70px 4vw;
          }

          .glitz-collection {

            padding:
              70px 4vw;
          }

          .glitz-footer {

            padding:
              30px 4vw;
          }

        }


        /* ======================================================
           TABLET
           ====================================================== */

        @media (max-width: 850px) {

          .glitz-navigation {

            padding:
              15px 20px;
          }


          .glitz-hero {

            display:
              grid;

            grid-template-columns:
              1fr 1fr;

            min-height:
              auto;
          }


          .glitz-hero-left {

            min-height:
              600px;

            padding:
              60px 35px;
          }


          .glitz-main-image {

            min-height:
              600px;
          }


          .glitz-main-image img {

            min-height:
              600px;
          }


          .glitz-hero-right {

            grid-column:
              1 / -1;

            display:
              grid;

            grid-template-columns:
              1fr 1fr;

            gap:
              25px;

            padding:
              40px 35px;
          }


          .glitz-redefined {

            margin-top:
              0;
          }


          .glitz-products {

            grid-template-columns:
              repeat(2, 1fr);
          }


          .glitz-product:last-child {

            grid-column:
              1 / -1;

            max-width:
              50%;

            margin:
              auto;
          }


          .glitz-empowerment {

            grid-template-columns:
              1fr 1fr;

            padding:
              70px 35px;
          }


          .glitz-empowerment-image {

            min-height:
              450px;
          }


          .glitz-pillars {

            grid-column:
              1 / -1;

            display:
              grid;

            grid-template-columns:
              repeat(3, 1fr);
          }


          .glitz-values {

            grid-template-columns:
              repeat(3, 1fr);
          }


          .glitz-value:nth-child(4),
          .glitz-value:nth-child(5) {

            grid-column:
              span 1;
          }


          .glitz-value:nth-child(3)::after,
          .glitz-value:nth-child(5)::after {

            display:
              none;
          }


          .glitz-footer {

            grid-template-columns:
              90px 1fr;

            padding:
              30px 35px;
          }


          .glitz-footer-links {

            grid-column:
              1 / -1;
          }

        }


        /* ======================================================
           MOBILE
           ====================================================== */

        @media (max-width: 600px) {

          .glitz-navigation {

            padding:
              12px 15px;
          }


          .glitz-nav-brand span {

            font-size:
              14px;

            letter-spacing:
              1px;
          }


          .glitz-nav-brand img {

            width:
              38px;

            height:
              38px;
          }


          .glitz-back-button {

            padding:
              8px 12px;

            font-size:
              10px;
          }


          .glitz-hero {

            display:
              flex;

            flex-direction:
              column;
          }


          .glitz-hero-left {

            min-height:
              auto;

            padding:
              65px 25px;

            align-items:
              center;

            text-align:
              center;

            border-right:
              none;
          }


          .glitz-logo {

            width:
              85px;

            height:
              85px;
          }


          .glitz-brand-name {

            font-size:
              68px;

            letter-spacing:
              -3px;
          }


          .glitz-brand-tagline {

            font-size:
              15px;
          }


          .glitz-divider {

            margin:
              22px auto;
          }


          .glitz-description {

            font-size:
              12px;

            line-height:
              1.8;
          }


          .glitz-slogan {

            text-align:
              left;

            width:
              100%;

            font-size:
              13px;
          }


          .glitz-main-image {

            width:
              100%;

            min-height:
              500px;
          }


          .glitz-main-image img {

            min-height:
              500px;
          }


          .glitz-hero-right {

            display:
              flex;

            padding:
              35px 20px;
          }


          .glitz-feature-box {

            padding:
              18px;
          }


          .glitz-feature {

            padding:
              12px 5px;
          }


          .glitz-collection {

            padding:
              60px 18px;
          }


          .glitz-products {

            display:
              flex;

            flex-direction:
              column;

            gap:
              20px;
          }


          .glitz-product:last-child {

            max-width:
              none;

            margin:
              0;
          }


          .glitz-product-image {

            height:
              320px;
          }


          .glitz-empowerment {

            display:
              flex;

            flex-direction:
              column;

            padding:
              60px 20px;

            gap:
              30px;
          }


          .glitz-empowerment-image {

            min-height:
              350px;
          }


          .glitz-empowerment-image img {

            min-height:
              350px;
          }


          .glitz-pillars {

            display:
              flex;

            flex-direction:
              column;

            gap:
              22px;
          }


          .glitz-values {

            grid-template-columns:
              repeat(2, 1fr);
          }


          .glitz-value {

            min-height:
              125px;

            padding:
              15px 8px;
          }


          .glitz-value::after {

            display:
              none;
          }


          .glitz-value:nth-child(odd) {

            border-right:
              1px solid
              rgba(212,175,55,0.2);
          }


          .glitz-value:last-child {

            grid-column:
              1 / -1;
          }


          .glitz-motto {

            padding:
              28px 18px;
          }


          .glitz-motto h2 {

            font-size:
              13px;

            letter-spacing:
              1px;
          }


          .glitz-footer {

            display:
              flex;

            flex-direction:
              column;

            text-align:
              center;

            padding:
              40px 20px;
          }


          .glitz-number {

            width:
              75px;

            height:
              75px;

            clip-path:
              none;

            border-radius:
              5px;

            font-size:
              38px;
          }


          .glitz-company-name {

            font-size:
              13px;
          }


          .glitz-company-tag {

            font-size:
              11px;
          }


          .glitz-footer-links {

            width:
              100%;

            grid-template-columns:
              repeat(2, 1fr);
          }


          .glitz-footer-item {

            border:
              none;

            border-top:
              1px solid
              rgba(212,175,55,0.15);

            padding:
              15px 5px;
          }

        }


        /* ======================================================
           SMALL MOBILE
           ====================================================== */

        @media (max-width: 390px) {

          .glitz-nav-brand span {

            display:
              none;
          }


          .glitz-brand-name {

            font-size:
              58px;
          }


          .glitz-main-image {

            min-height:
              420px;
          }


          .glitz-main-image img {

            min-height:
              420px;
          }


          .glitz-product-image {

            height:
              280px;
          }


          .glitz-values {

            grid-template-columns:
              1fr;
          }


          .glitz-value:nth-child(odd) {

            border-right:
              none;
          }


          .glitz-value:last-child {

            grid-column:
              auto;
          }

        }

      `}</style>


      {/* ========================================================
          NAVIGATION
          ======================================================== */}

      <nav className="glitz-navigation">

        <div className="glitz-nav-brand">

          <img
            src={glitzLogo}
            alt="Glitz"
          />

          <span>
            GLITZ
          </span>

        </div>


        <button
          className="glitz-back-button"
          onClick={() => navigate('/')}
        >

          <FaArrowLeft />

          BACK TO ALL SECTORS

        </button>

      </nav>


      {/* ========================================================
          HERO
          ======================================================== */}

      <section className="glitz-hero">


        {/* LEFT */}

        <div
          className="
            glitz-hero-left
            glitz-left-reveal
          "
        >

          <img
            src={glitzLogo}
            alt="Glitz Logo"
            className="glitz-logo"
          />


          <h1 className="glitz-brand-name">
            Glitz
          </h1>


          <p className="glitz-brand-tagline">
            An Absolute Impulse
          </p>


          <div className="glitz-divider"></div>


          <p className="glitz-description">

            Glitz is a premium lifestyle brand by
            Ashraf Group, curated for the modern
            woman who values style, quality and
            confidence.

            <br /><br />

            We bring together elegance, comfort
            and sophistication through our diverse
            range of carefully selected products.

          </p>


          <div className="glitz-slogan">

            STYLE THAT EMPOWERS.
            <br />

            QUALITY THAT INSPIRES.

          </div>

        </div>


        {/* CENTER IMAGE */}

        <div
          className="
            glitz-main-image
            glitz-scale
          "
        >

          <img
            src={glitz1}
            alt="Glitz Collection"
          />

        </div>


        {/* RIGHT */}

        <div
          className="
            glitz-hero-right
            glitz-right-reveal
          "
        >

          <div className="glitz-feature-box">


            <div className="glitz-feature">

              <div className="glitz-feature-icon">
                <FaGem />
              </div>

              <span className="glitz-feature-text">
                PREMIUM QUALITY
              </span>

            </div>


            <div className="glitz-feature">

              <div className="glitz-feature-icon">
                <GiTShirt />
              </div>

              <span className="glitz-feature-text">
                ELEGANT DESIGNS
              </span>

            </div>


            <div className="glitz-feature">

              <div className="glitz-feature-icon">
                <FaShieldAlt />
              </div>

              <span className="glitz-feature-text">
                TRUSTED BRAND
              </span>

            </div>


            <div className="glitz-feature">

              <div className="glitz-feature-icon">
                <FaFemale />
              </div>

              <span className="glitz-feature-text">
                WOMEN EMPOWERMENT
              </span>

            </div>


          </div>


          <div className="glitz-redefined">

            <small>
              BEAUTY. COMFORT. CONFIDENCE.
            </small>

            <h3>
              Redefined
            </h3>

          </div>

        </div>

      </section>


      {/* ========================================================
          COLLECTION
          ======================================================== */}

      <section className="glitz-collection">


        <div className="glitz-heading glitz-reveal">

          <span>
            DISCOVER THE COLLECTION
          </span>

          <h2>
            GLITZ SIGNATURE COLLECTION
          </h2>

          <div className="glitz-heading-line"></div>

        </div>


        <div className="glitz-products">


          {/* JEWELLERY */}

          <article
            className="
              glitz-product
              glitz-reveal
            "
          >

            <h3 className="glitz-product-title">
              ARTIFICIAL JEWELLERY
            </h3>

            <div className="glitz-product-image">

              <img
                src={glitz2}
                alt="Artificial Jewellery"
              />

            </div>

            <div className="glitz-product-body">

              <p>

                Exquisite designs that add sparkle
                to every moment. Crafted to perfection
                for every occasion.

              </p>

            </div>

          </article>


          {/* ABAYAS */}

          <article
            className="
              glitz-product
              glitz-reveal
            "
          >

            <h3 className="glitz-product-title">
              ABAYAS
            </h3>

            <div className="glitz-product-image">

              <img
                src={glitz3}
                alt="Abayas"
              />

            </div>

            <div className="glitz-product-body">

              <p>

                Modest. Modern. Majestic.

                <br /><br />

                A collection that celebrates grace,
                tradition and contemporary style.

              </p>

            </div>

          </article>


          {/* UNDER GARMENTS */}

          <article
            className="
              glitz-product
              glitz-reveal
            "
          >

            <h3 className="glitz-product-title">
              LADIES UNDER GARMENTS
            </h3>

            <div className="glitz-product-image">

              <img
                src={glitz4}
                alt="Ladies Under Garments"
              />

            </div>

            <div className="glitz-product-body">

              <p>

                Comfort that empowers.
                <br />

                Quality that cares.
                <br />

                Designed for your everyday confidence.

              </p>

            </div>

          </article>


        </div>

      </section>


      {/* ========================================================
          WOMEN EMPOWERMENT
          ======================================================== */}

      <section className="glitz-empowerment">


        {/* IMAGE */}

        <div
          className="
            glitz-empowerment-image
            glitz-left-reveal
          "
        >

          <img
            src={glitz5}
            alt="Women Empowerment"
          />

        </div>


        {/* CONTENT */}

        <div
          className="
            glitz-empowerment-content
            glitz-reveal
          "
        >

          <small>
            A PROJECT OF ASHRAF GROUP
          </small>


          <h2>
            WOMEN
            <br />
            EMPOWERMENT
          </h2>


          <div className="glitz-empowerment-line"></div>


          <p>

            Glitz is more than a brand – it is a
            movement towards building a stronger,
            independent and self-reliant community
            of women.

            <br /><br />

            We empower women by creating
            opportunities, skills and resources
            to help them achieve financial
            independence and a better tomorrow.

          </p>


          <div className="glitz-quote">

            <FaQuoteLeft
              className="glitz-quote-left"
            />

            <p>

              When women grow, families grow.
              <br />

              When families grow, communities thrive.
              <br />

              When communities thrive, nations prosper.

            </p>

            <FaQuoteRight
              className="glitz-quote-right"
            />

          </div>

        </div>


        {/* PILLARS */}

        <div
          className="
            glitz-pillars
            glitz-right-reveal
          "
        >


          <div className="glitz-pillar">

            <div className="glitz-pillar-icon">
              <FaUserGraduate />
            </div>

            <div>

              <h4>
                SKILL DEVELOPMENT
              </h4>

              <p>
                Training programs to build skills
                and enhance capabilities.
              </p>

            </div>

          </div>


          <div className="glitz-pillar">

            <div className="glitz-pillar-icon">
              <FaBriefcase />
            </div>

            <div>

              <h4>
                EMPLOYMENT OPPORTUNITIES
              </h4>

              <p>
                Providing sustainable work
                opportunities for women.
              </p>

            </div>

          </div>


          <div className="glitz-pillar">

            <div className="glitz-pillar-icon">
              <FaChartLine />
            </div>

            <div>

              <h4>
                FINANCIAL INDEPENDENCE
              </h4>

              <p>
                Supporting women to become
                self-reliant and confident.
              </p>

            </div>

          </div>


        </div>

      </section>


      {/* ========================================================
          VALUE PROPOSITION
          ======================================================== */}

      <section className="glitz-values glitz-reveal">


        <div className="glitz-value">

          <FaGem />

          <span>
            PREMIUM & TRENDY
            <br />
            PRODUCTS
          </span>

        </div>


        <div className="glitz-value">

          <FaCheckCircle />

          <span>
            STRICT QUALITY
            <br />
            STANDARDS
          </span>

        </div>


        <div className="glitz-value">

          <FaHandHoldingHeart />

          <span>
            CUSTOMER
            <br />
            SATISFACTION
          </span>

        </div>


        <div className="glitz-value">

          <FaShoppingBag />

          <span>
            ELEGANCE IN
            <br />
            EVERY DETAIL
          </span>

        </div>


        <div className="glitz-value">

          <FaGlobe />

          <span>
            GROWING WITH
            <br />
            GLOBAL TRENDS
          </span>

        </div>


      </section>


      {/* ========================================================
          MOTTO
          ======================================================== */}

      <section
        className="
          glitz-motto
          glitz-reveal
        "
      >

        <h2>

          EMPOWERING WOMEN.
          &nbsp;

          ENRICHING LIVES.
          &nbsp;

          ELEVATING FUTURES.

        </h2>

      </section>


      {/* ========================================================
          FOOTER
          ======================================================== */}

      <footer className="glitz-footer">


        <div className="glitz-number">
          21
        </div>


        <div>

          <span className="glitz-company-name">

            ASHRAF GROUP OF INDUSTRIES

          </span>

          <span className="glitz-company-tag">

            Building Legacy. Powering the Future.

          </span>

        </div>


        <div className="glitz-footer-links">


          <div className="glitz-footer-item">

            <FaBuilding />

            <span>
              DIVERSE
              <br />
              PORTFOLIO
            </span>

          </div>


          <div className="glitz-footer-item">

            <FaGlobe />

            <span>
              GLOBAL
              <br />
              PRESENCE
            </span>

          </div>


          <div className="glitz-footer-item">

            <FaShieldAlt />

            <span>
              TRUSTED
              <br />
              EXCELLENCE
            </span>

          </div>


          <div className="glitz-footer-item">

            <FaHandshake />

            <span>
              DELIVERING
              <br />
              VALUE
            </span>

          </div>


          <div className="glitz-footer-item">

            <FaChartLine />

            <span>
              GROWING
              <br />
              TOGETHER
            </span>

          </div>


        </div>

      </footer>


    </div>
  );
}